import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Stack, LinearProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Divider, Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  WarningAmber as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

const PontoXLSX = () => {
  const [file, setFile]                       = useState(null);
  const [isLoading, setIsLoading]             = useState(false);
  const [error, setError]                     = useState('');
  const [registros, setRegistros]             = useState([]);
  const [listaFuncionarios, setListaFuncionarios] = useState([]);
  const [openModal, setOpenModal]             = useState(false);
  const [indexAtual, setIndexAtual]           = useState(0);
  const [selecaoAtual, setSelecaoAtual]       = useState('');

  const fileInputRef = useRef(null);
  const { authenticatedFetch, logout } = useAuth();

  // Pré-seleciona o funcionário sugerido sempre que o modal avança
  useEffect(() => {
    if (openModal && registros[indexAtual]) {
      setSelecaoAtual(registros[indexAtual].ponto_funcionario_id ?? '');
    }
  }, [indexAtual, openModal]);

  const handleUpload = async (selectedFile) => {
    if (!selectedFile) return;
    setIsLoading(true);
    setError('');
    setRegistros([]);

    const formData = new FormData();
    formData.append('documento_ponto', selectedFile);

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/teste-xlsx-ler/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao processar a planilha.');
      }

      const data = await response.json();
      setRegistros(data.registros);
      setListaFuncionarios(data.lista_funcionarios);

      if (data.registros.length > 0) {
        setIndexAtual(0);
        setOpenModal(true);
      }
    } catch (err) {
      if (err.message.includes('Token')) logout();
      else setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmarVinculo = (idSelecionado) => {
    const nomeNaPlanilha = registros[indexAtual].nome_planilha;

    const novosRegistros = registros.map(reg =>
      reg.nome_planilha === nomeNaPlanilha
        ? { ...reg, ponto_funcionario_id: idSelecionado, status: 'OK' }
        : reg
    );
    setRegistros(novosRegistros);
    avancarModal(novosRegistros);
  };

  const avancarModal = (listaAtualizada = registros) => {
    const nomeAtual = listaAtualizada[indexAtual].nome_planilha;
    const proximoIndex = listaAtualizada.findIndex((reg, i) =>
      i > indexAtual &&
      reg.nome_planilha !== nomeAtual &&
      !listaAtualizada.slice(0, i).some(r => r.nome_planilha === reg.nome_planilha)
    );

    if (proximoIndex !== -1) {
      setIndexAtual(proximoIndex);
    } else {
      setOpenModal(false);
    }
  };

  const salvarNoBanco = async () => {
    setIsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-xlsx-salvar/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registros }),
      });
      if (response.ok) {
        const data = await response.json();
        alert(`${data.criados} criados, ${data.atualizados} atualizados.`);
        setRegistros([]);
        setFile(null);
      }
    } catch (err) {
      alert('Erro ao salvar: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const registroAtual  = registros[indexAtual];
  const nomesUnicos    = [...new Set(registros.map(r => r.nome_planilha))];
  const progressoModal = nomesUnicos.indexOf(registroAtual?.nome_planilha) + 1;

  const nomeFunc = (id) =>
    listaFuncionarios.find(f => f.id === id)?.nome || '???';

  return (
    <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Importação de Ponto (.xlsx)
      </Typography>

      {/* ÁREA DE UPLOAD */}
      {registros.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 6, textAlign: 'center', cursor: 'pointer', border: '2px dashed',
            borderColor: 'primary.main', bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => fileInputRef.current.click()}
        >
          <input
            type="file" hidden ref={fileInputRef} accept=".xlsx"
            onChange={(e) => handleUpload(e.target.files[0])}
          />
          <Stack alignItems="center" spacing={2}>
            <CloudUploadIcon sx={{ fontSize: 50, color: 'primary.main' }} />
            <Typography>Clique ou arraste a planilha de ponto aqui</Typography>
          </Stack>
        </Paper>
      )}

      {isLoading && <LinearProgress sx={{ mt: 2 }} />}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* TABELA DE RESUMO */}
      {registros.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Registros Processados ({registros.length})
          </Typography>

          <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
            {registros.map((reg, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5, display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', borderBottom: '1px solid #eee',
                  bgcolor: !reg.ponto_funcionario_id ? '#fff5f5' : 'inherit',
                }}
              >
                <Box>
                  <Typography variant="body2">
                    <strong>{reg.nome_planilha}</strong>
                    {' → '}
                    {nomeFunc(reg.ponto_funcionario_id)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reg.ponto_data}
                    {reg.ponto_entrada_chegada && ` | E: ${reg.ponto_entrada_chegada}`}
                    {reg.ponto_saida_almoco    && ` | A: ${reg.ponto_saida_almoco}`}
                    {reg.ponto_entrada_almoco  && ` | V: ${reg.ponto_entrada_almoco}`}
                    {reg.ponto_saida_saida     && ` | S: ${reg.ponto_saida_saida}`}
                  </Typography>
                </Box>
                {reg.status === 'OK'
                  ? <CheckIcon fontSize="small" color="success" />
                  : <WarningIcon fontSize="small" color="warning" />
                }
              </Box>
            ))}
          </Paper>

          {registros.some(r => !r.ponto_funcionario_id) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ainda há registros sem funcionário vinculado.
            </Alert>
          )}

          <Button
            variant="contained" fullWidth size="large"
            onClick={salvarNoBanco}
            disabled={registros.some(r => !r.ponto_funcionario_id) || isLoading}
          >
            Confirmar e Salvar no Banco
          </Button>
          <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => setRegistros([])}>
            Cancelar
          </Button>
        </Box>
      )}

      {/* MODAL DE CONFERÊNCIA */}
      <Dialog open={openModal} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Conferir Funcionário
          <Chip
            label={`${progressoModal} / ${nomesUnicos.length}`}
            size="small" sx={{ ml: 'auto' }}
          />
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Nome na planilha:
          </Typography>
          <Typography variant="h6" gutterBottom>
            "{registroAtual?.nome_planilha}"
          </Typography>

          {registroAtual?.score_func > 0 && registroAtual.score_func < 100 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Match automático com score {registroAtual.score_func}% — confirme se está correto.
            </Alert>
          )}

          {registroAtual?.score_func === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nenhum funcionário encontrado automaticamente. Selecione manualmente.
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Funcionário</InputLabel>
            <Select
              label="Funcionário"
              value={selecaoAtual}
              onChange={(e) => setSelecaoAtual(e.target.value)}
            >
              {listaFuncionarios.map(f => (
                <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Typography variant="caption" sx={{ flex: 1 }}>
            Será aplicado a todos os "{registroAtual?.nome_planilha}" desta planilha.
          </Typography>
          <Button onClick={() => setOpenModal(false)} color="inherit">Parar</Button>
          <Button
            variant="contained"
            onClick={() => confirmarVinculo(selecaoAtual)}
            disabled={!selecaoAtual}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PontoXLSX;